import {
  ComponentFactoryResolver,
  ComponentRef,
  Directive,
  ElementRef,
  Inject,
  OnDestroy,
  OnInit,
  Renderer2,
  ViewContainerRef
} from '@angular/core';
import {Subscription} from 'rxjs';
import {BlockSelection} from '@blocks/services/block-selection/block-selection.type';
import {BlockComponent} from '@blocks/block/block.component';
import {BlockSelectionService} from '@blocks/services/block-selection/block-selection.service';
import {Block} from '@paperlessio/sdk/api/models';
import {UserAvatarListItemComponent} from '@shared/user-avatar/user-avatar-list/user-avatar-list-item/user-avatar-list-item.component';
import {UserAvatarMode} from '@shared/user-avatar/user-avatar.type';
import {ToastService} from '@paperlessio/sdk/api/util';
import {BLOCKS_CONFIG, BlocksConfig} from '@blocks/blocks.config';

/**
 * Directive to be used on block components. It listens to the block selections changed events and checks if the parent
 * block is currently selected. If this is the case the css class "block-selection--{{indexColor}}" is set or removed
 * (see _block-selection.scss). The blocked is locked and the user avatar is shown on mouse over.
 */
@Directive({
  selector: '[blockSelection]'
})
export class BlockSelectionDirective implements OnInit, OnDestroy {
  private readonly cssClassName = 'block-selection'
  private block: Block;
  private subs: Subscription = new Subscription();
  private currentBlockSelection: BlockSelection;
  private componentRef: ComponentRef<UserAvatarListItemComponent>;
  private overlayElement: HTMLDivElement;

  get blockElement() {
    return this.elementRef.nativeElement;
  }

  constructor(private renderer: Renderer2,
              private elementRef: ElementRef,
              private viewContainerRef: ViewContainerRef,
              private componentFactoryResolver: ComponentFactoryResolver,
              private blockSelectionService: BlockSelectionService,
              private blockComponent: BlockComponent,
              @Inject(BLOCKS_CONFIG) public blocksConfig: BlocksConfig,
              private toastService: ToastService) {
  }

  ngOnInit() {
    this.block = this.blockComponent?.block;
    if (this.blocksConfig.selectable) {
      this.subscribeToBlockSelectionChangedEvents(this.onBlockSelectionChangedEvents);
    }
  }

  private subscribeToBlockSelectionChangedEvents = (observer: (blockSelections: BlockSelection[]) => void) =>
    this.subs.add(this.blockSelectionService.blockSelection$.subscribe(observer));

  private onBlockSelectionChangedEvents = (blockSelections: BlockSelection[]) => {
    if (this.currentBlockSelection) {
      this.removeSelection();
    }

    this.currentBlockSelection = blockSelections.find((blockSelection: BlockSelection) => blockSelection.isSelected(this.block?.id));

    if (this.currentBlockSelection) {
      this.addSelection(this.currentBlockSelection);
    }
  }

  private onOverlayClick = (mouseEvent: MouseEvent) => {
    this.toastService.error('blocks.selection.locked', {name: this.currentBlockSelection.name});
    mouseEvent.stopPropagation();
  }

  private onOverlayMouseEnter = (mouseEvent: MouseEvent) => {
    this.componentRef.instance.isVisible = true;
  }

  private onOverlayMouseLeave = (mouseEvent: MouseEvent) => {
    this.componentRef.instance.isVisible = false;
  }

  private addSelection(blockSelection: BlockSelection) {
    this.addSelectionRect(this.currentBlockSelection);

    const factory = this.componentFactoryResolver.resolveComponentFactory(UserAvatarListItemComponent);
    this.componentRef = this.viewContainerRef.createComponent(factory);
    this.componentRef.instance.mode = UserAvatarMode.selection;
    this.componentRef.instance.user = blockSelection.subscribedUser;
    this.componentRef.instance.isVisible = false;
    this.renderer.appendChild(this.elementRef.nativeElement, this.componentRef.location.nativeElement);

    this.overlayElement = this.renderer.createElement('div');
    this.overlayElement.addEventListener('click', this.onOverlayClick)
    this.overlayElement.addEventListener('mouseenter', this.onOverlayMouseEnter);
    this.overlayElement.addEventListener('mouseleave', this.onOverlayMouseLeave);

    this.renderer.addClass(this.overlayElement, this.cssClassName + '--overlay');
    this.renderer.appendChild(this.elementRef.nativeElement, this.overlayElement);
  }

  private removeSelection() {
    this.removeSelectionRect(this.currentBlockSelection);

    if(this.overlayElement) {
      this.overlayElement.removeEventListener('click', this.onOverlayClick);
      this.overlayElement.removeEventListener('mouseenter', this.onOverlayMouseEnter);
      this.overlayElement.removeEventListener('mouseleave', this.onOverlayMouseLeave);

      this.renderer.removeChild(this.elementRef.nativeElement, this.overlayElement);
    }

    if(this.componentRef) {
      this.renderer.removeChild(this.elementRef.nativeElement, this.componentRef.location.nativeElement);
    }

    this.overlayElement = null;
    this.componentRef = null;
  }

  private addSelectionRect(blockSelection: BlockSelection) {
    if(blockSelection) {
      this.renderer.addClass(this.blockElement, this.asSelectionRect(blockSelection));
    }
  }

  private removeSelectionRect(blockSelection: BlockSelection) {
    if(blockSelection) {
      this.renderer.removeClass(this.blockElement, this.asSelectionRect(blockSelection));
    }
  }

  private asSelectionRect(blockSelection: BlockSelection) {
    return blockSelection ? `${this.cssClassName}--${blockSelection.colorIndex}` : '';
  }

  ngOnDestroy() {
    this.subs.unsubscribe();

    if(this.currentBlockSelection) {
      this.removeSelection();
    }
  }
}
